import React from "react";
import styles from "../../pages/renewal/Renew.module.css";
import StatusIndicator from "./StatusIndicator";
import { hasVehicleInfo } from "../../utils/vehicleInfo";

/**
 * Vehicle details form section: engine number, color, year, make, model,
 * commercial type (CEI only), and plate number.
 *
 * @param {Object} props
 * @param {Object} props.input - Current form data
 * @param {Function} props.onInputChange - Input change handler
 * @param {Object} props.renewData - Redux renewal data
 * @param {Object} props.vehicleInfo - Canonical merged vehicle information
 * @param {Array} props.vehicleModel - Available vehicle models
 * @param {Array} props.vehicleColor - Available vehicle colors
 * @param {Array} props.ceiMake - CEI makes list
 * @param {Array} props.niconMake - NICON makes list
 * @param {Object} props.yearRange - { start, end, years }
 * @param {boolean} props.makeIdLoading - Whether make ID lookup is loading
 * @param {'success'|'error'|null} props.makeIdStatus - Make ID lookup status
 * @param {boolean} props.modelIdLoading - Whether model ID lookup is loading
 * @param {'success'|'error'|null} props.modelIdStatus - Model ID lookup status
 * @param {string} props.commercialType - Selected commercial type
 * @param {Function} props.onCommercialTypeChange - Commercial type change handler
 */
const VehicleFormFields = ({
  input,
  onInputChange,
  renewData,
  vehicleInfo,
  vehicleModel,
  vehicleColor,
  ceiMake,
  niconMake,
  salamMake,
  fgiMakes,
  agVehicleMakeModels,
  agpVehicleMakes,
  agpVehicleModels,
  agVehicleTypeOptions = [],
  agPolicyTypeOptions = [],
  yearRange,
  makeIdLoading,
  makeIdStatus,
  modelIdLoading,
  modelIdStatus,
  commercialType,
  onCommercialTypeChange,
}) => {
  const isNicon = renewData.insuranceType === "NICON";
  const isSalam = renewData.insuranceType === "SALAM";
  const isAg = renewData.insuranceType === "AG";
  const isAgp = renewData.insuranceType === "AGP";
  const isAgFamily = isAg || isAgp;
  const isFgi = renewData.insuranceType === "FGI";
  const isFgip = renewData.insuranceType === "FGIP";
  const isFgiFamily = isFgi || renewData.insuranceType === "FGIP";
  const makeArray = isFgiFamily
    ? fgiMakes
    : isNicon
      ? niconMake
      : isSalam
        ? salamMake
        : ceiMake;

  return (
    <div className={styles.wrapper}>
      {hasVehicleInfo(vehicleInfo) && (
        <h1 className={styles.wrapper_title}>
          Year: {vehicleInfo.year} - Make: {vehicleInfo.make} - Model:{" "}
          {vehicleInfo.model} - Color: {vehicleInfo.color}
        </h1>
      )}
      <div className={styles.input_container}>
        {/* Engine Number */}
        <input
          className={styles.input_field}
          type="text"
          required
          name="engineNo"
          onChange={onInputChange}
          value={input.engineNo}
          placeholder="Engine Number"
        />

        {/* Color Dropdown */}
        <select
          className={styles.select_field}
          name="color"
          value={input?.color}
          required
          onChange={onInputChange}
        >
          <option value="">Select Color</option>
          {vehicleColor?.map((option) => (
            <option
              key={option.vehicleColorId}
              value={isNicon ? option.colorName : option.vehicleColorId}
            >
              {option.colorName}
            </option>
          ))}
        </select>

        {/* Year Dropdown */}
        <select
          className={styles.input_field}
          name="year"
          required
          onChange={onInputChange}
          value={input.year}
        >
          <option value="">Select Year</option>
          {yearRange.years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        {isAg ? (
          <select
            className={styles.input_field}
            name="agVehicleMakeModelId"
            required
            onChange={onInputChange}
            value={input.agVehicleMakeModelId}
          >
            <option value="">Select Vehicle Make / Model</option>
            {agVehicleMakeModels?.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        ) : isAgp ? (
          <>
            <select
              className={styles.input_field}
              name="make"
              required
              onChange={onInputChange}
              value={input.make}
            >
              <option value="">Select Vehicle Make</option>
              {agpVehicleMakes?.map((make) => (
                <option key={make.id} value={make.id}>
                  {make.name}
                </option>
              ))}
            </select>

            <select
              className={styles.input_field}
              name="model"
              required
              onChange={onInputChange}
              value={input.model}
              disabled={!input.make}
            >
              <option value="">Select Vehicle Model</option>
              {agpVehicleModels?.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            {/* Make Dropdown */}
            <select
              className={isNicon ? styles.input_field : styles.modal_input}
              name="make"
              required
              onChange={onInputChange}
              value={input.make}
            >
              <option value="">
                {isNicon ? "Select Make" : "Select Vehicle Make"}
              </option>

              {isNicon ? (
                <>
                  <optgroup label="Popular Models">
                    {makeArray?.slice(0, 13).map((make) => (
                      <option key={make.id} value={make.id}>
                        {make.name}
                      </option>
                    ))}
                  </optgroup>

                  <optgroup label="Other Models">
                    {makeArray?.slice(12).map((make) => (
                      <option key={make.id} value={make.id}>
                        {make.name}
                      </option>
                    ))}
                  </optgroup>
                </>
              ) : (
                makeArray?.map((make) => (
                  <option key={make.id} value={isFgip ? make.code : make.id}>
                    {make.name}
                  </option>
                ))
              )}
            </select>

            {!isFgiFamily && (
              <StatusIndicator
                isLoading={makeIdLoading}
                status={makeIdStatus}
                loadingText="Fetching Make ID..."
                successText="Make ID found"
                errorText="Make ID not found"
              />
            )}

            {/* Model Dropdown */}
            <select
              className={styles.input_field}
              name="model"
              required
              onChange={onInputChange}
              value={input.model}
            >
              <option value="">Select Model</option>
              {vehicleModel?.map((option) => (
                <option
                  key={isNicon || isSalam || isFgiFamily ? option.id : option.vehicleModelId}
                  value={isNicon || isSalam || isFgiFamily ? option.id : option.vehicleModelId}
                >
                  {isNicon || isSalam || isFgiFamily ? option.name : option.modelName}
                </option>
              ))}
            </select>

            {!isFgiFamily && (
              <StatusIndicator
                isLoading={modelIdLoading}
                status={modelIdStatus}
                loadingText="Fetching Model ID..."
                successText="Model ID found"
                errorText="Model ID not found"
              />
            )}
          </>
        )}

        {isAgFamily && (
          <>
            <select
              className={styles.input_field}
              name="agVehicleType"
              required
              onChange={onInputChange}
              value={input.agVehicleType}
            >
              <option value="">Select Vehicle Type</option>
              {agVehicleTypeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>

            <select
              className={styles.input_field}
              name="agPolicyType"
              required
              onChange={onInputChange}
              value={input.agPolicyType}
            >
              <option value="">Select Policy Type</option>
              {agPolicyTypeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </>
        )}

        {/* Commercial Type (CEI only) */}
        {renewData?.productsDetails?.cei_policy_type === "Commercial" &&
          renewData?.insuranceType === "CEI" && (
            <select
              className={styles.input_field}
              name="commercialType"
              required
              onChange={onCommercialTypeChange}
              value={commercialType}
            >
              <option value="">Select Commercial Type</option>
              <option value="mini bus">Mini Bus</option>
              <option value="mini trucks">Mini Trucks</option>
              <option value="suv">SUV</option>
              <option value="commercial cars">Commercial Cars</option>
              <option value="pickup">Pickup</option>
            </select>
          )}

        {(isSalam || isFgiFamily) && (
          <input
            className={styles.input_field}
            type="text"
            name="nin"
            required={isSalam}
            onChange={onInputChange}
            value={input.nin}
            placeholder={isFgiFamily ? "NIN (optional)" : "NIN"}
          />
        )}

        {/* Plate Number */}
        <input
          className={styles.input_field}
          type="text"
          placeholder="Plate Number"
          name="plate_no"
          required
          onChange={onInputChange}
          value={input.plate_no}
        />
      </div>
    </div>
  );
};

export default VehicleFormFields;
